import { Link } from "@tanstack/react-router";
import { useLanguage, useT } from "@/lib/i18n/provider";
import { type ReactNode } from "react";
import { BusinessContact } from "@/components/policies/BusinessContact";

const POLICIES = [
  { to: "/policies/shipping-policy", key: "pol.navShipping" },
  { to: "/policies/refund-policy", key: "pol.navRefund" },
  { to: "/policies/return-exchange-policy", key: "pol.navReturnExchange" },
  { to: "/policies/privacy-policy", key: "pol.navPrivacy" },
  { to: "/policies/terms-of-service", key: "pol.navTerms" },
  { to: "/policies/trucoins-terms", key: "pol.navTrucoins" },
  { to: "/policies/affiliate-terms", key: "pol.navAffiliate" },
] as const;

export function PolicyLayout({
  title,
  updated,
  children,
}: {
  title: string;
  updated?: string;
  children: ReactNode;
}) {
  const t = useT();
  const { lang } = useLanguage();
  return (
    <div className="mx-auto max-w-5xl px-4 py-12 md:px-8 md:py-16">
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand">{t("pol.eyebrow")}</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-brand-deep md:text-5xl">
        {title}
      </h1>
      {updated && (
        <p className="mt-2 text-xs text-muted-foreground">{t("pol.lastUpdated")} {updated}</p>
      )}
      {lang === "hi-en" && (
        <p className="mt-3 rounded-xl border border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
          {t("pol.bilingualNote")}
        </p>
      )}

      <div className="mt-8 grid gap-10 md:grid-cols-[220px_1fr]">
        <aside className="policy-navigation md:sticky md:top-24 md:self-start">
          <nav className="rounded-2xl border border-border bg-card/60 p-4">
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {t("pol.allPolicies")}
            </p>
            <ul className="space-y-2">
              {POLICIES.map((p) => (
                <li key={p.to}>
                  <Link
                    to={p.to}
                    className="block text-sm text-muted-foreground transition-colors hover:text-brand"
                    activeProps={{ className: "text-brand font-semibold" }}
                  >
                    {t(p.key)}
                  </Link>
                </li>
              ))}
              <li><Link to="/policies/money-back-promise" className="block text-sm text-muted-foreground hover:text-brand">60-Day Money-Back Promise</Link></li>
              <li><Link to="/data-deletion" className="block text-sm text-muted-foreground hover:text-brand">Privacy and deletion requests</Link></li>
            </ul>
            <button type="button" onClick={() => window.print()} className="mt-4 text-sm underline">Print policy</button>
          </nav>
        </aside>

        <article className="policy-prose space-y-6 text-[15px] leading-relaxed text-foreground/85">
          {children}
          <BusinessContact />
          <div className="mt-12 rounded-2xl border border-border bg-brand-soft/40 p-6">
            <p className="font-display text-base font-semibold text-brand-deep">{t("pol.needHelpTitle")}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("pol.needHelpBody", {
                email: "hello@truwe.in",
              }).split("hello@truwe.in").map((part, i, arr) => (
                <span key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <a href="mailto:hello@truwe.in" className="font-semibold text-brand underline">
                      hello@truwe.in
                    </a>
                  )}
                </span>
              ))}
            </p>
          </div>
        </article>
      </div>

      <style>{`
        .policy-prose h2 { font-weight: 600; font-size: 1.125rem; margin-top: 1.75rem; scroll-margin-top: 6rem; color: hsl(var(--foreground)); }
        @media print { .policy-navigation { display: none !important; } .policy-prose { display: block !important; color: #000 !important; } .policy-prose h2 { break-after: avoid; } .policy-prose a { color: #000 !important; } }
        .policy-prose h3 { font-weight: 600; font-size: 1rem; margin-top: 1.5rem; margin-bottom: 0.25rem; color: hsl(var(--foreground)); }
        .policy-prose p { margin-bottom: 0.75rem; }
        .policy-prose ul { list-style: disc; padding-left: 1.25rem; margin-bottom: 0.75rem; }
        .policy-prose ul li { margin-bottom: 0.35rem; }
        .policy-prose a { color: var(--brand, #1E5EFF); text-decoration: underline; }
      `}</style>
    </div>
  );
}
